import axios from "axios";

import { isEmergencySubject, subjectIdOf } from "./billing-subject";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";

export function trimId(id) {
  return String(id ?? "").replace(/\s+/g, "").trim();
}

export function resolveAdmissionId(patient) {
  const id =
    patient?.admission_id ||
    patient?.admissionId ||
    patient?.ipd_admission_id ||
    patient?.encounter_id ||
    sessionStorage.getItem("ipd_admission_id") ||
    "";
  return trimId(id);
}

// Full banner context (name / bed / doctor / payer) for the selected admission.
// Only the id used to be persisted, so a reload or a deep-link into another
// billing screen could still fetch data but had nothing to show in the header —
// hence the old "Restored admission" placeholder.
const ADMISSION_CTX_KEY = "ipd_admission_ctx";

export function persistAdmissionContext(patient) {
  const admissionId = resolveAdmissionId(patient);
  if (!admissionId) return;
  sessionStorage.setItem("ipd_admission_id", admissionId);
  if (!patient || typeof patient !== "object") return;
  try {
    sessionStorage.setItem(
      ADMISSION_CTX_KEY,
      JSON.stringify({ ...patient, admission_id: admissionId })
    );
  } catch {
    // Non-serialisable/oversized context — the id alone still drives the APIs.
  }
}

/**
 * Rehydrate the selected patient from sessionStorage. Returns the cached
 * context when it belongs to the stored admission, an id-only stub otherwise,
 * and null when no admission is in session at all.
 */
export function restoreAdmissionContext(admissionId) {
  const id = trimId(admissionId) || trimId(sessionStorage.getItem("ipd_admission_id"));
  if (!id) return null;
  try {
    const cached = JSON.parse(sessionStorage.getItem(ADMISSION_CTX_KEY) || "null");
    if (cached && trimId(cached.admission_id) === id) {
      return {
        ...cached,
        admission_id: id,
        admissionId: id,
        encounter_id: cached.encounter_id || String(id),
      };
    }
  } catch {
    // Corrupt cache — fall through to the id-only context.
  }
  return { admission_id: id, admissionId: id, name: "Restored admission", encounter_id: String(id) };
}

export function clearAdmissionContext() {
  sessionStorage.removeItem("ipd_admission_id");
  sessionStorage.removeItem(ADMISSION_CTX_KEY);
}

/** Consultant names come back bare ("Sruti Mehra") — prefix the title once. */
export function formatDoctorName(name) {
  const raw = String(name ?? "").trim();
  if (!raw || raw === "—") return "";
  if (/^(dr\.?|prof\.?|doctor)\s/i.test(raw)) return raw;
  return `Dr. ${raw}`;
}

export const PAYMENT_MODE_TO_API = {
  UPI: "UPI",
  Card: "CARD",
  Cash: "CASH",
  "Bank Transfer": "BANK_TRANSFER",
  Cheque: "CHEQUE",
  Wallet: "WALLET",
};

export const PAYMENT_MODE_FROM_API = {
  UPI: "UPI",
  CARD: "Card",
  CASH: "Cash",
  BANK_TRANSFER: "Bank Transfer",
  CHEQUE: "Cheque",
  WALLET: "Wallet",
};

export function formatPaymentModeLabel(value) {
  if (value == null || value === "") return "—";
  const raw = String(value).trim();
  const normalized = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (PAYMENT_MODE_FROM_API[normalized]) return PAYMENT_MODE_FROM_API[normalized];
  if (PAYMENT_MODE_FROM_API[raw]) return PAYMENT_MODE_FROM_API[raw];
  return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Remove trailing amount suffix e.g. "Admission advance — ₹3,000" → "Admission advance" */
export function stripEmbeddedAmountFromText(text) {
  if (!text || text === "—") return text;
  const cleaned = String(text).replace(/\s*[—–-]\s*₹[\d,.]+(?:\.\d{1,2})?\s*$/u, "").trim();
  return cleaned || text;
}

export function getBillingHeaders(includeJson = false) {
  const headers = {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
    "App-Mode":
      sessionStorage.getItem("APP_MODE") ||
      sessionStorage.getItem("appMode") ||
      "SaaS",
    "Org-Code":
      sessionStorage.getItem("ORG_CODE") ||
      sessionStorage.getItem("orgCode") ||
      "",
    Username:
      sessionStorage.getItem("userName") ||
      sessionStorage.getItem("username") ||
      "",
    "User-Access-Key":
      sessionStorage.getItem("USER_ACCESS_KEY") ||
      sessionStorage.getItem("accessKey") ||
      "",
  };
  if (includeJson) headers["Content-Type"] = "application/json";
  return headers;
}

function billingBase(admissionId) {
  return `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing`;
}

function unwrapList(data, keys = []) {
  if (Array.isArray(data)) return data;
  for (const key of ["data", "results", "items", ...keys]) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object" && Array.isArray(val.items)) {
      return val.items;
    }
  }
  return [];
}

function unwrapObject(data) {
  if (!data || typeof data !== "object") return data;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

function formatGender(gender) {
  if (!gender) return "—";
  const value = String(gender).toUpperCase();
  if (value === "MALE" || value === "M") return "M";
  if (value === "FEMALE" || value === "F") return "F";
  return gender;
}

function formatPayerType(value) {
  if (!value) return null;
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Who is paying, for the patient banner — the corporate / insurer / TPA /
 * scheme name when the admission carries one, qualified by the payer type.
 * Falls back to the bare type ("Self Pay") and to "" when nothing is set.
 * Source: ipd_admission → admission_details (manage_admission.py).
 */
export function formatPayerLabel(admissionDetails) {
  const d = admissionDetails || {};
  const type = formatPayerType(d.payer_type);
  const name =
    d.corporate_name ||
    d.insurance_provider ||
    d.tpa_name ||
    d.government_scheme ||
    "";
  if (name && type) return `${name} (${type})`;
  return name || type || "";
}

function ageFromDob(dob) {
  if (!dob) return "—";
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return "—";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return String(age);
}

/** Flatten EMR screen payload: invoice_view + split_billing + settlement_status */
export function normalizeInvoiceScreenResponse(raw) {
  if (!raw || typeof raw !== "object") return raw;

  const view = raw.invoice_view || {};
  const inv = view.invoice || raw.invoice || {};
  const encounter = view.encounter || raw.encounter || {};
  const summary = view.summary || raw.totals || {};
  const tenant = view.tenant || raw.tenant || raw.hospital || {};

  const invoiceId = inv.invoice_id || inv.id || raw.invoice_id;
  const invoiceStatus = inv.invoice_status || raw.header?.invoice_status || "";

  return {
    ...raw,
    invoice: {
      ...inv,
      id: invoiceId,
      invoice_id: invoiceId,
      invoice_status: invoiceStatus,
      invoice_number: inv.invoice_number || raw.invoice_number,
      invoice_type: inv.invoice_type || raw.invoice_type,
      issued_at: inv.issued_at || inv.invoice_date,
      due_date: inv.due_date,
      gross_amount: inv.gross_amount ?? summary.gross_amount,
      discount_amount: inv.discount_amount ?? summary.discount_amount,
      tax_amount: inv.tax_amount ?? summary.tax_amount,
      net_amount: inv.net_amount ?? summary.net_amount,
      paid_amount: inv.paid_amount ?? summary.paid_amount,
      outstanding_amount: inv.outstanding_amount ?? summary.outstanding_amount,
      items: view.items || inv.items || [],
    },
    tenant,
    hospital: tenant,
    patient: view.patient || raw.patient || {},
    encounter,
    doctor: {
      name: encounter.provider_name || raw.doctor?.name || raw.doctor_name || "—",
      specialty: encounter.department_name || raw.doctor?.specialty || "—",
      ward:
        [encounter.ward_name, encounter.bed_number].filter(Boolean).join(" / ") ||
        encounter.room_number ||
        raw.doctor?.ward ||
        "—",
    },
    payer: view.payer || raw.payer || raw.meta || {},
    summary,
    items: view.items || inv.items || raw.items || [],
    split_billing: raw.split_billing || view.split_billing || [],
    settlement_status: raw.settlement_status,
    totals: raw.totals,
    deposits_adjusted: raw.totals?.deposits_adjusted,
  };
}

/**
 * Collapse noisy upstream errors (raw nginx/HTML gateway pages, stack traces)
 * into a single clean line. Backend sometimes wraps a 502/504 upstream body into
 * `message`, so this runs on both success-Failed and thrown-error paths.
 */
export function sanitizeErrorMessage(raw, status) {
  let msg = String(raw ?? "").trim();
  const gateway = /(50[234])\s*bad gateway|bad gateway|gateway time-?out|<html|<head|<center|nginx/i;
  if (gateway.test(msg) || [502, 503, 504].includes(status)) {
    const code =
      status && [502, 503, 504].includes(status)
        ? status
        : (msg.match(/\b(50[234])\b/) || [])[1] || "502";
    return `Service temporarily unavailable (${code}). Please retry in a moment.`;
  }
  // Strip any stray tags and collapse whitespace, then cap length.
  msg = msg.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (msg.length > 160) msg = `${msg.slice(0, 157)}…`;
  return msg || "Request failed";
}

// No money is taken across a shut counter, so a collection with no open cash-counter
// session is refused. Say where to go, not just what went wrong.
export const CASH_COUNTER_CLOSED_HINT =
  "Please open the cash counter before taking payment — Billing → Cash Counter.";

async function billingRequestUrl(method, url, body) {
  try {
    const res = await axios({
      method,
      url,
      data: body,
      headers: getBillingHeaders(body != null),
    });
    if (res.data?.status === "Failed") {
      const wrapped = new Error(sanitizeErrorMessage(res.data.message || "Request failed"));
      wrapped.status = res.data.status_code || res.data.code;
      wrapped.cashCounterRequired = res.data.cash_counter_required === true;
      throw wrapped;
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    // Re-throw an already-wrapped/sanitized error untouched.
    if (err && err.status !== undefined && !err.response) throw err;
    const status = err.response?.status;
    const rawMsg =
      err.response?.data?.message ||
      err.response?.data?.detail ||
      (typeof err.response?.data === "string" ? err.response.data : "") ||
      err.message ||
      "Request failed";
    const wrapped = new Error(sanitizeErrorMessage(rawMsg, status));
    wrapped.status = status;
    wrapped.code = err.response?.data?.code || err.response?.data?.error_code;
    wrapped.cashCounterRequired = err.response?.data?.cash_counter_required === true;
    throw wrapped;
  }
}

// Admission-scoped: /ipd_admission/{id}/billing{path}
function billingRequest(method, admissionId, path, body) {
  return billingRequestUrl(method, `${billingBase(admissionId)}${path}`, body);
}

// Tenant-scoped: /ipd/billing{path} — for resources that aren't tied to a
// patient/admission (e.g. cash counters & scrolls, which belong to a counter).
function billingTenantRequest(method, path, body) {
  return billingRequestUrl(method, `${API_ROOT}ipd/billing${path}`, body);
}

// ── IPD Admissions (patient search) ───────────────────────────────────────────

export async function listAllocatedAdmissions(status = "Allocated") {
  try {
    const res = await axios.get(
      `${API_ROOT}ipd_admission/?status=${encodeURIComponent(status)}`,
      { headers: getBillingHeaders() }
    );
    if (res.data?.status === "Failed") {
      throw new Error(res.data.message || "Failed to load admissions");
    }
    const list = res.data?.data ?? res.data?.ipd_admissions ?? [];
    return Array.isArray(list) ? list : [];
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    const msg =
      err.response?.data?.message ||
      err.message ||
      "Failed to load admissions";
    throw new Error(msg);
  }
}

export function mapAdmissionForSearch(row) {
  const p = row?.patient || {};
  const patientName =
    p.name ||
    [p.first_name, p.last_name].filter(Boolean).join(" ").trim() ||
    "Unknown patient";
  return {
    admission_id: row.admission_id,
    status: row.status,
    admitted_at: row.admitted_at,
    bed_id: row.bed_id,
    bed_type_code: row.bed_type_code,
    bed_type_name: row.bed_type_name,
    department_id: row.department_id,
    ward_name: row.ward_name,
    doctor_name: row.doctors?.admitting?.name || row.doctor_name || "—",
    patient_id: p.patient_id || "—",
    // UHID is the identifier shown to billing staff; not every record has one,
    // so fall back to the internal patient_id rather than rendering a dash.
    uhid: p.uhid || p.patient_id || "—",
    patient_name: patientName,
    gender: p.gender,
    payer_type: row.admission_details?.payer_type,
    payer_label: formatPayerLabel(row.admission_details),
  };
}

export function filterAdmissionsByQuery(rows, query) {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const text = [
      row.patient_name,
      row.patient_id,
      row.uhid,
      String(row.admission_id),
      // An emergency visit has no admission number and is identified by its
      // visit number — the one the row actually displays. Absent on admissions,
      // where it drops out below. (ADR 0009)
      row.visit_number,
      row.bed_id,
      row.doctor_name,
      row.ward_name,
      row.department_id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return text.includes(q);
  });
}

export function admissionToPatientContext(row) {
  const mapped = row.patient_name ? row : mapAdmissionForSearch(row);
  return {
    admission_id: mapped.admission_id,
    admissionId: mapped.admission_id,
    name: mapped.patient_name,
    patient_id: mapped.patient_id,
    uhid: mapped.uhid || mapped.patient_id,
    bed: mapped.bed_id,
    // department_id is an opaque "<slug>_<facility_id>" key; fall back to it only
    // when the bed has no room/ward, so this never renders blank.
    ward: mapped.ward_name || mapped.department_id,
    bed_type: mapped.bed_type_name || mapped.bed_type_code || "",
    // The code is kept alongside the display name because tariffs and rate-card
    // items are scoped by bed_type_code, not by the human-readable name.
    bed_type_code: mapped.bed_type_code || "",
    doctor: mapped.doctor_name,
    gender: mapped.gender,
    payer_type: mapped.payer_type || "",
    payer: mapped.payer_label || "",
    encounter_id: String(mapped.admission_id),
  };
}

/**
 * A context restored from the admission id alone (no cache, e.g. first load
 * after a hard refresh in a new tab) carries no patient details. Once the
 * allocated-admissions list arrives, swap in the real row so the banner never
 * sits on the "Restored admission" stub. Returns the same reference when there
 * is nothing to hydrate, so callers can use it directly in an effect.
 *
 * Only admissions are restored from an id. An emergency visit is always selected
 * from the list with its details, and a closed one has no bed — hydrating it
 * would match the first row without an admission id, another patient's visit.
 */
export function hydrateRestoredPatient(patient, admissions) {
  if (!patient || isEmergencySubject(patient)) return patient;
  if (!Array.isArray(admissions) || admissions.length === 0) return patient;
  const complete = patient.name && patient.name !== "Restored admission" && patient.bed;
  if (complete) return patient;
  const id = resolveAdmissionId(patient);
  const row = admissions.find((a) => trimId(a.admission_id) === id);
  if (!row) return patient;
  return {
    ...admissionToPatientContext(row),
    encounter_id: patient.encounter_id || String(id),
  };
}

// ── Invoices ────────────────────────────────────────────────────────────────

export async function listInvoices(admissionId) {
  const data = await billingRequest("GET", admissionId, "/invoices/");
  return unwrapList(data, ["invoices"]);
}

export async function getInvoiceView(admissionId, invoiceId) {
  const data = await billingRequest(
    "GET",
    admissionId,
    `/invoices/${trimId(invoiceId)}/view/`
  );
  return unwrapObject(data);
}

export async function getInvoiceScreen(admissionId, invoiceId) {
  const data = await billingRequest(
    "GET",
    admissionId,
    `/invoices/${trimId(invoiceId)}/screen/`
  );
  return normalizeInvoiceScreenResponse(unwrapObject(data));
}

export async function generateInvoice(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/invoices/", payload);
  return unwrapObject(data);
}

/**
 * Preview charges for an admission (GET .../ipd_admission/<id>/billing/bill-preview/).
 * Returns { lines, subtotal, net_payable, line_adhoc_discount_amount, bill_total_discount_amount,
 * package_discount_amount }.
 * Lines/totals live under `bill_preview` per the API guide (older shape kept as fallback).
 */
export async function getBillPreview(admissionId) {
  const data = await billingRequest("GET", admissionId, "/bill-preview/");
  const obj = unwrapObject(data);
  const preview = obj.bill_preview || obj;
  const list = unwrapList(preview, ["lines", "line_items", "items", "bill_lines", "preview", "charges"]);
  const lines = list.map((item, idx) => ({
    id: item.billing_ledger_id || item.ledger_entry_number || item.id || item.line_number || idx + 1,
    description: item.description || item.service_name || item.name || "—",
    dept: item.dept || item.category || item.department || item.service_group || "—",
    service_code: item.service_code || item.code || null,
    // Per-line group/classification/unique codes (from the service master) so discount
    // targets match the charged bed-specific lines directly.
    group_code: item.group_code || null,
    classification_code: item.classification_code || null,
    unique_code: item.unique_code || null,
    medical_service_id:
      item.emr_medical_service_id || item.medical_service_id || item.medicalServiceId || item.service_id || null,
    qty: num(item.quantity ?? item.qty ?? 1),
    rate: num(item.unit_price ?? item.rate ?? (num(item.gross_amount) / (num(item.quantity) || 1))),
    amount: num(item.net_amount ?? item.amount ?? item.gross_amount ?? item.netAmount),
  }));
  return {
    lines,
    subtotal: num(preview.gross_amount ?? lines.reduce((s, l) => s + l.amount, 0)),
    net_payable: num(preview.net_payable ?? preview.bill_net_after_lines ?? 0),
    line_adhoc_discount_amount: num(preview.line_adhoc_discount_amount),
    bill_total_discount_amount: num(preview.bill_total_discount_amount),
    // An adhoc discount given on a PACKAGE reduces the package's own price, so it lands
    // here rather than on a line or on the bill total. Dropping it left the ledger's own
    // discount looking un-applied, so the summary listed it twice — once as the bill's
    // discount and again as "pending Apply" — and under-stated the payable by that much.
    package_discount_amount: num(preview.package_discount_amount),
  };
}

// Issue = final settlement (DRAFT → ISSUED + FINAL). Uses POST (not PATCH) — some
// environments block PATCH in the CORS preflight while POST works (same reason as
// updateAdhocDiscount/revoke). The EMR issue endpoint accepts either verb.
export async function issueInvoice(admissionId, invoiceId) {
  const data = await billingRequest(
    "POST",
    admissionId,
    `/invoices/${trimId(invoiceId)}/issue/`
  );
  return unwrapObject(data);
}

// ── Payments ──────────────────────────────────────────────────────────────────

export async function listPayments(admissionId) {
  const data = await billingRequest("GET", admissionId, "/payments/");
  return unwrapList(data, ["payments"]);
}

export async function collectPayment(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/payments/", payload);
  return unwrapObject(data);
}

/**
 * GET /ipd_admission/{id}/billing/pan/?invoice_id= — PAN on file for this admission plus whether
 * the current bill needs one (₹1,00,000 and above). Used by the Collect Payment screen to prefill
 * and require the PAN field.
 */
export async function getPatientPan(admissionId, invoiceId) {
  const suffix = invoiceId ? `/pan/?invoice_id=${trimId(invoiceId)}` : "/pan/";
  const data = await billingRequest("GET", admissionId, suffix);
  const o = unwrapObject(data);
  return {
    panNumber: o.pan_number || null,
    present: Boolean(o.present),
    billTotal: num(o.bill_total),
    panRequired: Boolean(o.pan_required),
  };
}

export async function allocatePayment(admissionId, paymentId, payload) {
  const data = await billingRequest(
    "POST",
    admissionId,
    `/payments/${trimId(paymentId)}/allocate/`,
    payload
  );
  return unwrapObject(data);
}

// ── Invoices & payments by billing subject ──────────────────────────────────
// The same calls for either kind of subject: an admission goes to its
// /ipd_admission/{id}/billing endpoints, an emergency visit to
// /emergency/visits/{id}/billing, which answer with the same shapes. Only the
// operations an emergency visit supports have a subject form (billing-subject.js).

function subjectRequest(method, patient, path, body) {
  const base = isEmergencySubject(patient)
    ? `${API_ROOT}emergency/visits/${trimId(subjectIdOf(patient))}/billing`
    : billingBase(subjectIdOf(patient));
  return billingRequestUrl(method, `${base}${path}`, body);
}

export async function listInvoicesFor(patient) {
  return unwrapList(await subjectRequest("GET", patient, "/invoices/"), ["invoices"]);
}

export async function getInvoiceScreenFor(patient, invoiceId) {
  const data = await subjectRequest("GET", patient, `/invoices/${trimId(invoiceId)}/screen/`);
  return normalizeInvoiceScreenResponse(unwrapObject(data));
}

export async function generateInvoiceFor(patient, payload) {
  return unwrapObject(await subjectRequest("POST", patient, "/invoices/", payload));
}

export async function issueInvoiceFor(patient, invoiceId) {
  return unwrapObject(await subjectRequest("POST", patient, `/invoices/${trimId(invoiceId)}/issue/`));
}

export async function listPaymentsFor(patient) {
  return unwrapList(await subjectRequest("GET", patient, "/payments/"), ["payments"]);
}

export async function collectPaymentFor(patient, payload) {
  return unwrapObject(await subjectRequest("POST", patient, "/payments/", payload));
}

// ── Deposits ──────────────────────────────────────────────────────────────────

export async function listDeposits(admissionId) {
  const data = await billingRequest("GET", admissionId, "/deposits/");
  return unwrapList(data, ["deposits"]);
}

export async function getDeposit(admissionId, depositId) {
  const data = await billingRequest(
    "GET",
    admissionId,
    `/deposits/${trimId(depositId)}/`
  );
  return unwrapObject(data);
}

export async function createDeposit(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/deposits/", payload);
  return unwrapObject(data);
}

/** Printable advance-payment receipt for a single deposit. */
export async function getDepositReceipt(admissionId, depositId) {
  const data = await billingRequest(
    "GET",
    admissionId,
    `/deposits/${trimId(depositId)}/receipt/`
  );
  const obj = unwrapObject(data);
  return obj?.receipt || obj;
}

// ── Insurance approval (co-pay) ─────────────────────────────────────────────

/** Total bill, insurer-approved amount, and patient payable (= total − approved). */
export async function getInsuranceApproval(admissionId) {
  const data = await billingRequest("GET", admissionId, "/insurance-approval/");
  return unwrapObject(data);
}

/** Set the insurer-approved (LOA) amount → reduces the patient's payable. */
export async function setInsuranceApproval(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/insurance-approval/", payload);
  return unwrapObject(data);
}

// ── Billing dashboard (command center) ──────────────────────────────────────

/** KPI + weekly-collections summary for the IPD billing dashboard. */
export async function getBillingDashboardSummary() {
  const data = await billingTenantRequest("GET", "/dashboard/summary/");
  const obj = unwrapObject(data);
  return obj?.summary || obj || {};
}

/** High-outstanding IPD accounts table for the dashboard. */
export async function getHighOutstandingAccounts(limit = 20, offset = 0) {
  const data = await billingTenantRequest(
    "GET",
    `/dashboard/high-outstanding/?limit=${limit}&offset=${offset}`
  );
  const obj = unwrapObject(data);
  return Array.isArray(obj?.items) ? obj.items : [];
}

export async function allocateDeposit(admissionId, depositId, payload) {
  const data = await billingRequest(
    "POST",
    admissionId,
    `/deposits/${trimId(depositId)}/allocate/`,
    payload
  );
  return unwrapObject(data);
}

export async function refundDeposit(admissionId, depositId, payload) {
  const data = await billingRequest(
    "POST",
    admissionId,
    `/deposits/${trimId(depositId)}/refund/`,
    payload
  );
  return unwrapObject(data);
}

export async function listDepositsRefunds(admissionId) {
  const data = await billingRequest("GET", admissionId, "/deposits-refunds/");
  // Backend returns refunds under `refund_requests`; keep the old keys as fallbacks.
  return unwrapList(data, ["refund_requests", "refunds", "transactions", "items"]);
}

/** Transaction rows from GET .../deposits-refunds/ `transaction_history` */
export async function getDepositTransactionHistory(admissionId) {
  const data = await billingRequest("GET", admissionId, "/deposits-refunds/");
  const screen = unwrapObject(data);
  if (Array.isArray(screen?.transaction_history)) return screen.transaction_history;
  if (Array.isArray(screen?.transactions)) return screen.transactions;
  return [];
}

export async function adjustDepositsRefunds(admissionId, payload) {
  const data = await billingRequest(
    "POST",
    admissionId,
    "/deposits-refunds/adjust/",
    payload
  );
  return unwrapObject(data);
}

// ── Ledger & Audit ────────────────────────────────────────────────────────────

/**
 * Fetch the billing ledger + audit trail for an admission.
 *
 * GET ipd_admission/{admissionId}/billing/ledger-audit/?entry_filter=<all|debits|credits|adjustments>
 * Summary cards always reflect the full encounter; only `entries` respect the filter.
 *
 * @param {string} admissionId
 * @param {string} [entryFilter] all | debits | credits | adjustments (omit → all)
 * @returns {Promise<object>} {
 *   status, admission_id, billing_account_id,
 *   cards: { debits, credits, net_outstanding, last_activity_at },  // amounts are strings
 *   entries: [...], total, actions
 * }
 */
export async function getLedgerAudit(admissionId, entryFilter) {
  const qs = entryFilter ? `?entry_filter=${encodeURIComponent(entryFilter)}` : "";
  return billingRequest("GET", admissionId, `/ledger-audit/${qs}`);
}

// One ledger row (API → table row). Field names are defensive — the ledger-audit
// entry shape isn't fully documented, so common aliases are all accepted.
export function mapLedgerEntry(e, idx = 0) {
  const iso =
    e.timestamp || e.event_time || e.created_at || e.entry_time || e.date || null;
  let date = "—";
  let time = "";
  if (iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      date = String(iso);
    } else {
      date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
      time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
    }
  }
  const debit = num(e.debit ?? e.debit_amount);
  const credit = num(e.credit ?? e.credit_amount);
  return {
    id:
      e.id ||
      e.ledger_entry_id ||
      e.ledger_entry_number ||
      e.entry_number ||
      idx + 1,
    date,
    time,
    ref:
      e.reference ||
      e.ref ||
      e.reference_number ||
      e.document_reference ||
      e.entry_reference ||
      "—",
    description:
      e.description || e.narration || e.memo || e.particulars || "—",
    debit: debit > 0 ? debit : null,
    credit: credit > 0 ? credit : null,
    balance: num(e.balance ?? e.running_balance ?? e.balance_after),
    by:
      e.by || e.actor || e.created_by || e.operator || e.performed_by || e.user || "—",
  };
}

// ── Service hierarchy + adhoc discounts (Additional Discount modal) ───────────
//
// Three-level hierarchy used by the picker:
//   service_group  →  service_classification  →  medical_service
// Endpoints/shapes per additional-discount-ui-api.md.
const SERVICE_GROUPS_ENDPOINT = "ipd/billing/service-groups/";
const SERVICE_CLASSIFICATIONS_ENDPOINT = "ipd/billing/service-classifications/";
const MEDICAL_SERVICES_ENDPOINT = "ipd/billing/service-catalog/services/";

async function masterGet(endpoint, params) {
  try {
    const res = await axios.get(`${API_ROOT}${endpoint}`, {
      headers: getBillingHeaders(),
      params,
    });
    if (res.data?.status === "Failed") {
      throw new Error(res.data.message || "Failed to load");
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    throw new Error(
      err.response?.data?.message || err.message || "Failed to load service data"
    );
  }
}

export async function listServiceGroups(admissionId) {
  const params = { active_only: true };
  if (admissionId) params.admission_id = trimId(admissionId);
  const data = await masterGet(SERVICE_GROUPS_ENDPOINT, params);
  const list = unwrapList(data, ["service_groups", "groups"]);
  return list.map((g) => ({
    id: g.id ?? g.service_group_id,
    name: g.name ?? g.group_name ?? g.group_code ?? "—",
    code: g.group_code ?? null,
  }));
}

export async function listServiceClassifications(groupId, admissionId) {
  const params = { active_only: true };
  if (groupId) params.group_id = groupId;
  if (admissionId) params.admission_id = trimId(admissionId);
  const data = await masterGet(SERVICE_CLASSIFICATIONS_ENDPOINT, params);
  const list = unwrapList(data, ["service_classifications", "classifications"]);
  return list.map((c) => ({
    id: c.id ?? c.service_classification_id,
    name: c.name ?? c.classification_name ?? c.classification_code ?? "—",
    code: c.classification_code ?? null,
    groupId: c.service_group_id ?? groupId,
  }));
}

// Search the service catalog by name/code, optionally narrowed by group +
// classification (+ bed type / sample — Service Master tariff dimensions).
//   GET .../ipd/billing/service-catalog/services/?name=<term>&group_id=&classification_id=&bed_type_code=&sample_id=&unique_code=&admission_id=&limit=200
export async function listMedicalServices({ search, groupId, classificationId, bedTypeCode, sampleId, uniqueCode, admissionId, doctorConsultation, doctorId } = {}) {
  const params = { limit: 200 };
  if (search) params.name = search;
  // A doctor's consultation fee belongs to a doctor, not to a service group, so it has no
  // group or classification and the ordinary group-then-classification browse never finds it.
  // Asking for consultations lists them on their own, and the other scope params are moot.
  if (doctorConsultation) params.doctor_consultation = true;
  if (doctorId) params.doctor_id = doctorId;
  if (groupId) params.group_id = groupId;
  if (classificationId) params.classification_id = classificationId;
  if (bedTypeCode) params.bed_type_code = bedTypeCode;
  if (sampleId) params.sample_id = sampleId;
  if (uniqueCode) params.unique_code = uniqueCode;
  if (admissionId) params.admission_id = trimId(admissionId);
  const data = await masterGet(MEDICAL_SERVICES_ENDPOINT, params);
  const list = unwrapList(data, ["services", "service_list", "medical_services"]);
  return list.map((s) => ({
    id: s.id ?? s.medical_service_id,
    name: s.name ?? s.service_name ?? "—",
    code: s.service_code ?? s.code ?? null,
    // Logical Service Master identity — prefer this as a corporate rate-card
    // line's scope_code so one rule covers every bed variant.
    uniqueCode: s.unique_code ?? null,
    bedTypeCode: s.bed_type_code ?? null,
    sampleId: s.sample_id ?? null,
    chargeModifiable: s.charge_modifiable ?? null,
    quantityModifiable: s.quantity_modifiable ?? null,
    classificationId: s.service_classification_id ?? null,
    groupId: s.service_group_id ?? null,
    fee: s.fee != null ? num(s.fee) : null,
    isDoctorConsultation: Boolean(s.is_doctor_consultation),
    doctorName: s.doctor_name ?? null,
  }));
}

// A GROUP-level corporate rate must cover every service in the group, but the
// catalog endpoint requires a classification (group_id + classification_id). So
// fetch each classification's services and union them. Per-classification
// failures are swallowed so one bad classification doesn't blank the whole group.
export async function listMedicalServicesForGroup(groupId) {
  if (!groupId) return [];
  const classifications = await listServiceClassifications(groupId);
  const batches = await Promise.all(
    classifications.map((c) =>
      listMedicalServices({ groupId, classificationId: c.id }).catch(() => [])
    )
  );
  return batches.flat();
}

// Predefined discount reasons. TODO: replace the mock with the real endpoint
// (e.g. GET .../billing/adhoc-discounts/reasons/) once it's available.
const MOCK_DISCOUNT_REASONS = [
  { id: "GOODWILL", label: "Management approved goodwill discount" },
  { id: "CAMP", label: "Health camp / community outreach" },
  { id: "STAFF", label: "Staff / dependent concession" },
  { id: "CORPORATE", label: "Corporate tie-up rate" },
  { id: "INSURANCE", label: "Insurance / TPA negotiated rate" },
  { id: "FINANCIAL_AID", label: "Financial assistance (patient hardship)" },
  { id: "SERVICE_RECOVERY", label: "Service recovery / complaint resolution" },
];

// List selectable discount reasons (mock for now — resolves async like a real fetch).
export async function listDiscountReasons() {
  return new Promise((resolve) => setTimeout(() => resolve(MOCK_DISCOUNT_REASONS), 150));
}

// Create one adhoc-discount config (POST .../billing/adhoc-discounts/).
export async function createAdhocDiscount(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/adhoc-discounts/", payload);
  return unwrapObject(data);
}

// Create several configs at once (POST .../billing/adhoc-discounts/batch/).
// Upsert: re-saving the same scope (group/classification/service id) updates the
// existing active config instead of duplicating.
export async function createAdhocDiscountsBatch(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/adhoc-discounts/batch/", payload);
  return unwrapObject(data);
}

// Commit saved configs (POST .../billing/adhoc-discounts/apply/).
export async function applyAdhocDiscounts(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/adhoc-discounts/apply/", payload);
  return unwrapObject(data);
}

// Normalize one saved adhoc-discount config (API → UI).
function mapAdhocDiscount(d) {
  return {
    id: d.id ?? d.adhoc_discount_id ?? d.discount_id,
    level: d.discount_level,                                  // TOTAL | GROUP | CLASSIFICATION | SERVICE | PACKAGE
    type: d.discount_type,                                    // AMOUNT | PERCENTAGE
    value: d.discount_value != null ? num(d.discount_value) : 0,
    amount: d.discount_amount != null ? num(d.discount_amount) : null,
    reason: d.reason ?? "",
    approvedBy: d.approved_by ?? "",
    groupId: d.emr_service_group_id ?? null,
    classificationId: d.emr_service_classification_id ?? null,
    serviceId: d.emr_medical_service_id ?? null,
    packageAssignmentId: d.package_assignment_id ?? null,
    groupName: d.emr_service_group_name ?? d.group_name ?? null,
    classificationName: d.emr_service_classification_name ?? d.classification_name ?? null,
    serviceName: d.emr_medical_service_name ?? d.service_name ?? null,
    packageName: d.package_name ?? d.emr_package_name ?? null,
    // Human label for the discount's target — prefer the level-specific name.
    targetName:
      d.target_name ??
      d.package_name ??
      d.emr_medical_service_name ?? d.service_name ??
      d.emr_service_classification_name ?? d.classification_name ??
      d.emr_service_group_name ?? d.group_name ??
      null,
    active: d.active ?? d.is_active ?? true,
    // Approval gate. A discount worth more than the auto-approve limit is parked at
    // PENDING and cannot reach the ledger until a billing manager approves it.
    approvalStatus: d.approval_status ?? "NOT_REQUIRED",
    approvalRequestId: d.approval_request_id ?? null,
  };
}

// Does this discount still need a manager before it can be applied?
export function needsDiscountApproval(discount) {
  return String(discount?.approvalStatus ?? "").toUpperCase() === "PENDING";
}

// Was this discount refused? It will never apply until it is edited or replaced.
export function isDiscountRejected(discount) {
  return String(discount?.approvalStatus ?? "").toUpperCase() === "REJECTED";
}

// List saved/committed adhoc-discount configs.
//   GET .../billing/adhoc-discounts/?active_only=true  → { items: [...] }
export async function listAdhocDiscounts(admissionId, { activeOnly = true } = {}) {
  const data = await billingRequest("GET", admissionId, `/adhoc-discounts/${activeOnly ? "?active_only=true" : ""}`);
  return unwrapList(data, ["items", "adhoc_discounts", "discounts"]).map(mapAdhocDiscount);
}

// Update one config's type/value/reason (POST .../billing/adhoc-discounts/{id}/update/).
// Pass `reapply_ledger: true` to also re-sync the ledger when the discount is
// already applied. Uses POST (not PATCH) — some environments block PATCH in CORS
// preflight while POST works (same as revoke).
export async function updateAdhocDiscount(admissionId, discountId, payload) {
  const data = await billingRequest("POST", admissionId, `/adhoc-discounts/${discountId}/update/`, payload);
  return unwrapObject(data);
}

// Remove one config (POST .../billing/adhoc-discounts/{id}/revoke/).
//   reapplyLedger=false → before apply (config only); true → after apply (re-syncs ledger).
export async function revokeAdhocDiscount(admissionId, discountId, { reapplyLedger = true } = {}) {
  const data = await billingRequest("POST", admissionId, `/adhoc-discounts/${discountId}/revoke/`, { reapply_ledger: reapplyLedger });
  return unwrapObject(data);
}

// ── Discount approval gate ────────────────────────────────────────────────────
// A discount at or below the auto-approve limit applies straight away. Above it the
// discount sits at PENDING and a billing manager has to approve it first. The limit and
// who may approve are configured on the billing side (billing_approval_policy), so the
// UI reads the state rather than hard-coding the threshold.

// What the discount is worth right now and whether it can be applied yet.
//   GET .../billing/adhoc-discounts/{id}/approval-state/
//   → { effective_amount, auto_approve_limit, approval_status, approval_required, applicable }
export async function getDiscountApprovalState(admissionId, discountId) {
  const data = await billingRequest("GET", admissionId, `/adhoc-discounts/${discountId}/approval-state/`);
  const obj = unwrapObject(data) ?? {};
  return {
    discountId: obj.discount_id ?? discountId,
    effectiveAmount: num(obj.effective_amount),
    autoApproveLimit: num(obj.auto_approve_limit),
    approvalStatus: obj.approval_status ?? "NOT_REQUIRED",
    approvalRequired: Boolean(obj.approval_required),
    applicable: Boolean(obj.applicable),
  };
}

// Send an above-limit discount to a billing manager. `reason` is mandatory.
//   POST .../billing/adhoc-discounts/{id}/approval-requests/
export async function requestDiscountApproval(admissionId, discountId, reason) {
  const data = await billingRequest("POST", admissionId, `/adhoc-discounts/${discountId}/approval-requests/`, { reason });
  return unwrapObject(data);
}

// Approve or reject a pending request. The billing policy decides who is allowed —
// a supervisor is refused with "Approver role level too low", not blocked here.
//   POST .../billing/adhoc-discount-approvals/{requestId}/decide/
export async function decideDiscountApproval(admissionId, requestId, { approve, reason }) {
  const data = await billingRequest("POST", admissionId, `/adhoc-discount-approvals/${requestId}/decide/`, {
    approve: Boolean(approve),
    reason,
  });
  return unwrapObject(data);
}

/**
 * Approve or reject a charge dated before today. Approving is what puts it on the bill.
 *
 * Tenant-wide rather than per-admission - the manager answers these from one queue covering
 * every patient - so admissionId is accepted and ignored, to match the shape the Approvals
 * screen calls every decision with.
 */
export async function decideBackdatedCharge(_admissionId, requestId, { approve, reason }) {
  const { data } = await axios.post(
    `${API_ROOT}ipd/billing/backdated-charges/${trimId(requestId)}/decide/`,
    { approve: Boolean(approve), reason },
    { headers: getBillingHeaders(false) }
  );
  if (data?.status === "Failed") {
    throw new Error(sanitizeErrorMessage(data.message || "Could not record the decision"));
  }
  return unwrapObject(data);
}

// ── Billing manager dashboard ─────────────────────────────────────────────────
// Tenant-wide, manager-only. The API refuses anyone below role level 3, so a
// non-manager gets a Failed status rather than an empty screen.
//   GET /ipd/billing/manager-dashboard/[?include_charges=false]
export async function getManagerDashboard({ includeCharges = true } = {}) {
  const qs = includeCharges ? "" : "?include_charges=false";
  const { data } = await axios.get(`${API_ROOT}ipd/billing/manager-dashboard/${qs}`, {
    headers: getBillingHeaders(false),
  });
  if (data?.status === "Failed") {
    const err = new Error(sanitizeErrorMessage(data.message || "Could not load the dashboard"));
    err.status = data.status_code || data.code;
    throw err;
  }
  const s = data?.summary || {};
  return {
    roleName: data?.role_name || "",
    roleLevel: data?.role_level ?? null,
    summary: {
      billCancellations: Number(s.bill_cancellations || 0),
      billCancellationsAmount: num(s.bill_cancellations_amount),
      refunds: Number(s.refunds || 0),
      refundsAmount: num(s.refunds_amount),
      approvedDiscounts: Number(s.approved_discounts || 0),
      approvedDiscountsAmount: num(s.approved_discounts_amount),
      rejectedDiscounts: Number(s.rejected_discounts || 0),
      rejectedDiscountsAmount: num(s.rejected_discounts_amount),
      cancelledServices: Number(s.cancelled_services || 0),
      cancelledServicesAmount: num(s.cancelled_services_amount),
    },
    billCancellations: (data?.bill_cancellations || []).map(mapApprovalRow),
    refunds: (data?.refunds || []).map(mapApprovalRow),
    approvedDiscounts: (data?.approved_discounts || []).map(mapApprovalRow),
    rejectedDiscounts: (data?.rejected_discounts || []).map(mapApprovalRow),
    // Flat list of what changed on the bills after a cancellation. Authoritative — it
    // covers every capture path (OT, emergency, pharmacy, inventory) and carries
    // additions as well as removals, which the per-row cancelledCharges cannot.
    serviceChanges: (data?.service_changes || []).map(mapServiceChange),
  };
}

function mapServiceChange(c) {
  return {
    id: c.ledger_id,
    action: String(c.action || "REMOVED").toUpperCase(),
    serviceName: c.service_name || "—",
    serviceCode: c.service_code || "",
    category: c.category || "",
    amount: num(c.amount),
    quantity: c.quantity ?? null,
    patientName: c.patient_name || "",
    admissionId: c.admission_id ?? null,
    invoiceNumber: "",
    receiptNumber: "",
    requestedAt: c.when || c.cancelled_at || null,
  };
}

function mapApprovalRow(r) {
  return {
    id: r.approval_request_id,
    requestNumber: r.request_number,
    context: r.approval_context,
    status: r.request_status,
    statusLabel: r.status_label || r.request_status,
    amount: num(r.amount),
    reason: r.reason || "",
    requestedAt: r.requested_at || null,
    admissionId: r.admission_id ?? null,
    patientName: r.patient_name || "",
    invoiceNumber: r.invoice_number || "",
    receiptNumber: r.receipt_number || "",
    // Services deleted from the bill after it was cancelled (invoice cancellations only).
    cancelledCharges: (r.cancelled_charges || []).map((c) => ({
      id: c.ledger_id,
      serviceName: c.service_name || "—",
      serviceCode: c.service_code || "",
      category: c.category || "",
      amount: num(c.amount),
      cancelledAt: c.cancelled_at || null,
    })),
    cancelledChargesTotal: num(r.cancelled_charges_total),
  };
}

// Clear all configs + adhoc ledger rows (POST .../billing/adhoc-discounts/reset/).
export async function resetAdhocDiscounts(admissionId, { reapplyLedger = true } = {}) {
  const data = await billingRequest("POST", admissionId, "/adhoc-discounts/reset/", { reapply_ledger: reapplyLedger });
  return unwrapObject(data);
}

// ── Charge Capture ────────────────────────────────────────────────────────────
//
// Endpoints per charge-capture-ledger-ui-api.md:
//   GET  ipd_admission/{id}/billing/charge-capture/         (main screen)
//   GET  ipd/billing/charge-capture/services/               (Add Charge search)
//   POST ipd_admission/{id}/billing/charges/                (single)
//   POST ipd_admission/{id}/billing/charge-capture/bulk-post/ (modal, multiple)
//   POST ipd_admission/{id}/billing/charges/{eventId}/cancel/ (cancel w/ reason)
const CHARGE_CAPTURE_SERVICES_ENDPOINT = "ipd/billing/charge-capture/services/";

// One category pill (API → { id, label }). Accepts a plain string ("RADIOLOGY")
// or an object ({ id, label } / { code, name }).
function mapCategoryFilter(cat) {
  if (typeof cat === "string") {
    return { id: cat, label: cat.charAt(0) + cat.slice(1).toLowerCase() };
  }
  const id = cat.id ?? cat.code ?? cat.value ?? cat.category;
  return { id, label: cat.label ?? cat.name ?? cat.display ?? String(id) };
}

// Ensure an "All" pill leads the list — `available_categories` from the API
// only lists the concrete categories, not the default "All" filter.
function withAllCategory(cats) {
  if (cats.some((c) => String(c.id).toUpperCase() === "ALL")) return cats;
  return [{ id: "ALL", label: "All" }, ...cats];
}

// ISO timestamp → "DD Mon YYYY, HH:MM" for the DATE column.
function fmtChargeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date}, ${time}`;
}

// One charge-capture item (API → charge-capture table row).
function mapChargeItem(item) {
  const source = String(item.source || "").toLowerCase();
  const isAuto = Boolean(source) && source !== "manual";
  return {
    id: item.charge_event_id || item.id,
    charge_event_id: item.charge_event_id || item.id,
    time: fmtChargeTime(item.time),
    charge_date: item.charge_date || null,
    code: item.code || "—",
    description: item.description || item.service || "—",
    serviceGroup: item.group || "—",
    classification: item.classification || "—",
    medicalService: item.service || item.description || "—",
    dept: item.group || item.category || "—",
    category: item.category || "MISC",
    qty: num(item.qty),
    rate: num(item.rate),
    amount: num(item.amount),
    source,
    status: item.status || "",
    by: isAuto ? "Auto" : "Manual",
    auto: isAuto,
    // Corporate contract snapshot (present only when the row was priced from a
    // corporate rate card). `rate`/`amount` above are already the corporate
    // (payable) values; these expose the standard rack figure + saving.
    standardUnitPrice:
      item.standard_unit_price != null ? num(item.standard_unit_price) : null,
    corporateDiscountAmount:
      item.corporate_discount_amount != null ? num(item.corporate_discount_amount) : null,
    pricingSource: item.pricing_source || null,
    pricingApplied:
      item.pricing_applied === true ||
      String(item.pricing_source || "").toUpperCase().includes("CORPORATE"),
    // Service Master flags on the posted charge's tariff row. Default to
    // "editable" when absent so already-posted charges aren't newly locked
    // by rows the backend hasn't started returning the flag for yet.
    quantityModifiable: item.quantity_modifiable !== false,
    // Package allocation, present only once a package is assigned to the
    // admission. Null on every row when there is no package.
    packagePricingStatus: item.package_pricing_status || null,
    packageCode: item.package?.package_code || null,
  };
}

/**
 * Charge-capture main screen.
 * GET ipd_admission/{admissionId}/billing/charge-capture/?today=&category=&q=&limit=&offset=
 * @returns {Promise<{ totals, categoryFilters:[{id,label}], defaultChargeDate, items:[...] }>}
 */
export async function getChargeCapture(
  admissionId,
  { today = true, category = "ALL", q, limit, offset } = {}
) {
  const params = new URLSearchParams();
  params.set("today", today ? "true" : "false");
  if (category) params.set("category", category);
  if (q) params.set("q", q);
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  const data = await billingRequest(
    "GET",
    admissionId,
    `/charge-capture/?${params.toString()}`
  );
  const obj = unwrapObject(data);
  const totals = obj.totals || {};
  return {
    totals: {
      todaysTotal: num(totals.todays_total),
      autoCaptured: num(totals.auto_captured),
      manualEntries: num(totals.manual_entries),
      pendingApproval: num(totals.pending_approval),
    },
    categoryFilters: withAllCategory(
      (
        Array.isArray(obj.available_categories)
          ? obj.available_categories
          : Array.isArray(obj.category_filters)
            ? obj.category_filters
            : []
      ).map(mapCategoryFilter)
    ),
    defaultChargeDate: obj.default_charge_date || null,
    items: unwrapList(obj, ["items"]).map(mapChargeItem),
  };
}

/**
 * Patient-aware charge-capture service search (Add Charge + Orders investigation).
 * GET ipd/billing/charge-capture/services/?name=&admission_id=&group_code=&group_id=&limit=
 *
 * `admission_id` scopes tariffs to the patient's bed type (+ bed-less generics).
 * Pass `groupCode` or `groupId` to filter one service group (id wins if both sent).
 * `rate`/`fee` are the same value — bind to the Rate field; lock when
 * `charge_modifiable` / `quantity_modifiable` are false.
 *
 * Signature stays backward-compatible: searchChargeCaptureServices(name, limit, admissionId)
 * or searchChargeCaptureServices(name, { limit, admissionId, groupCode, groupId }).
 */
export async function searchChargeCaptureServices(name, limitOrOpts = 50, admissionId) {
  const opts =
    limitOrOpts && typeof limitOrOpts === "object"
      ? limitOrOpts
      : { limit: limitOrOpts, admissionId };
  const params = { limit: opts.limit ?? 50 };
  if (name) params.name = name;
  const admId = opts.admissionId ?? admissionId;
  if (admId) params.admission_id = trimId(admId);
  // id wins over code when both are sent (backend rule).
  if (opts.groupId != null && opts.groupId !== "") {
    params.group_id = opts.groupId;
  } else if (opts.groupCode) {
    params.group_code = String(opts.groupCode).toUpperCase();
  }
  const data = await masterGet(CHARGE_CAPTURE_SERVICES_ENDPOINT, params);
  const list = unwrapList(data, ["services"]);
  return list.map((s) => {
    const medicalServiceId = s.medical_service_id ?? s.id;
    return {
      id: medicalServiceId,
      medical_service_id: medicalServiceId,
      name: s.name ?? "—",
      description: s.description ?? s.name ?? "",
      code: s.service_code ?? s.code ?? null,
      service_code: s.service_code ?? s.code ?? null,
      rate: num(s.rate ?? s.fee),
      fee: num(s.fee ?? s.rate),
      unit: s.unit ?? null,
      service_type: s.service_type ?? s.category_chip ?? null,
      categoryChip: s.category_chip ?? null,
      groupName: s.service_group_name ?? null,
      groupId: s.service_group_id ?? null,
      classificationName: s.service_classification_name ?? null,
      // Service Master identity + billing-time flags (charge-capture/services/
      // now returns these per the ipd-service-master-flow.md rollout).
      uniqueCode: s.unique_code ?? null,
      bedTypeCode: s.bed_type_code ?? null,
      sampleId: s.sample_id ?? null,
      // Undocumented/older environments may omit these — default to today's
      // behavior (rate always locked, qty always editable) so nothing regresses.
      chargeModifiable: s.charge_modifiable === true,
      quantityModifiable: s.quantity_modifiable !== false,
      _raw: s,
    };
  });
}

/** Map Orders Investigation category label → charge-capture group_code. */
export function investigationCategoryToGroupCode(category) {
  const key = String(category || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (!key || key === "other" || key === "all" || key === "all_departments") return null;
  const aliases = {
    laboratory: "LABORATORY",
    lab: "LABORATORY",
    radiology: "RADIOLOGY",
    imaging: "RADIOLOGY",
    microbiology: "MICROBIOLOGY",
    pathology: "PATHOLOGY",
    cardiology: "CARDIOLOGY",
  };
  if (aliases[key]) return aliases[key];
  return String(category).trim().toUpperCase().replace(/\s+/g, "_");
}

/** Post a single charge (POST .../billing/charges/). */
export async function postCharge(admissionId, payload) {
  // Trigger doctor-payout calc at capture. The billing screen relies on this flag
  // (it doesn't depend on the server-side auto-calc env switch, which can be off).
  // Caller can still override by passing calculate_doctor_payout explicitly.
  const data = await billingRequest("POST", admissionId, "/charges/", {
    calculate_doctor_payout: true,
    ...payload,
  });
  return unwrapObject(data);
}

/**
 * Post multiple charges from the Add Charge modal.
 * POST .../billing/charge-capture/bulk-post/  body: { charges: [{ medical_service_id, quantity, charge_date, remarks }] }
 */
export async function bulkPostCharges(admissionId, charges) {
  // Per-item calculate_doctor_payout — the bulk endpoint processes each charge
  // independently, so the payout trigger must ride on each row (see postCharge note).
  const withPayout = (Array.isArray(charges) ? charges : []).map((c) => ({
    calculate_doctor_payout: true,
    ...c,
  }));
  const data = await billingRequest(
    "POST",
    admissionId,
    "/charge-capture/bulk-post/",
    { charges: withPayout }
  );
  return unwrapObject(data);
}

/**
 * Update one charge's quantity / remarks.
 * POST .../billing/charges/{chargeEventId}/update/  body: { quantity, remarks }
 * Use the charge-capture row's `charge_event_id` — not `medical_service_id`.
 */
export async function updateCharge(admissionId, chargeEventId, payload) {
  const data = await billingRequest(
    "POST",
    admissionId,
    `/charges/${trimId(chargeEventId)}/update/`,
    payload
  );
  return unwrapObject(data);
}

/**
 * Raw charge-event log (GET .../billing/charges/) — CHARGE, CANCELLATION and
 * DISCOUNT rows, each row's `id` being the charge_event_id. Unlike
 * getChargeCapture this is unaggregated, which is what a reversal needs in order
 * to find the original charge and see whether it was already cancelled.
 */
export async function getChargeEvents(admissionId) {
  const data = await billingRequest("GET", admissionId, "/charges/");
  return unwrapList(data, ["charges"]);
}

/**
 * Cancel one charge with a reason.
 * POST .../billing/charges/{chargeEventId}/cancel/  body: { reason }
 * Use the charge-capture row's `charge_event_id` — not `medical_service_id`.
 */
export async function cancelCharge(admissionId, chargeEventId, reason) {
  const data = await billingRequest(
    "POST",
    admissionId,
    `/charges/${trimId(chargeEventId)}/cancel/`,
    { reason }
  );
  return unwrapObject(data);
}

// ── Inventory master lists (read-only) ────────────────────────────────────────
//
// Thin readers over /app/api/inventory/* — used by the package builder's
// pharmacy/inventory bucket scope pickers and by pharmacy charge-capture scope
// tagging. Master lists only; no inventory-service writes. Same auth headers /
// base as every other billing master read (masterGet → getBillingHeaders).
const INVENTORY_MATERIAL_GROUPS_ENDPOINT = "inventory/material-groups/";
const INVENTORY_ITEM_CLASSES_ENDPOINT = "inventory/item-classes/";
const INVENTORY_CATEGORIES_ENDPOINT = "inventory/categories/";
const INVENTORY_ITEMS_ENDPOINT = "inventory/items/";

export async function listInventoryMaterialGroups() {
  const data = await masterGet(INVENTORY_MATERIAL_GROUPS_ENDPOINT);
  return unwrapList(data, ["material_groups", "items"]).map((g) => ({
    id: g.id ?? g.material_group_id,
    code: g.group_code ?? g.code ?? null,
    name: g.group_name ?? g.name ?? g.group_code ?? "—",
  }));
}

export async function listInventoryItemClasses() {
  const data = await masterGet(INVENTORY_ITEM_CLASSES_ENDPOINT);
  return unwrapList(data, ["item_classes", "items"]).map((c) => ({
    id: c.id ?? c.item_class_id,
    code: c.class_code ?? c.code ?? null,
    name: c.class_name ?? c.name ?? c.class_code ?? "—",
  }));
}

export async function listInventoryCategories() {
  const data = await masterGet(INVENTORY_CATEGORIES_ENDPOINT);
  return unwrapList(data, ["categories", "items"]).map((c) => ({
    id: c.id ?? c.category_id,
    code: c.category_code ?? c.code ?? null,
    name: c.category_name ?? c.name ?? c.category_code ?? "—",
    isDrugCategory: c.is_drug_category ?? null,
  }));
}

/** GET inventory/items/?search= — item picker for granular scope / exclusions. */
export async function searchInventoryItems(search, limit = 50) {
  const params = { limit };
  if (search) params.search = search;
  const data = await masterGet(INVENTORY_ITEMS_ENDPOINT, params);
  return unwrapList(data, ["items"]).map((it) => ({
    id: it.id ?? it.item_id,
    code: it.item_code ?? it.code ?? null,
    name: it.item_name ?? it.name ?? it.item_code ?? "—",
    itemType: it.item_type ?? null,
    materialGroupId: it.material_group_id ?? null,
    itemClassId: it.item_class_id ?? null,
    categoryId: it.category_id ?? null,
  }));
}

// ── Pharmacy / inventory charge capture ───────────────────────────────────────
//
// POST ipd_admission/{id}/billing/pharmacy-charge-capture/ posts one charge per
// item batch through the standard charge path (rules / corporate / ledger) and
// tags each with inventory scope so the package engine's capped pharmacy /
// inventory bucket can fold matching charges (and honour exclusions). Scope
// fields (all optional) are read per-item by the backend:
//   item_type ("medicine"|"consumable"|"asset"), is_drug_category (bool),
//   material_group_code, item_class_code, inventory_category_code, inventory_item_code.

/**
 * Resolve a selected inventory item (a searchInventoryItems row) into the scope
 * codes the bucket matches on. Group/class/category arrive as ids on the item,
 * so pass the loaded master lists to map id → code.
 */
export function deriveInventoryScope(item, { materialGroups = [], itemClasses = [], categories = [] } = {}) {
  if (!item) return {};
  const byId = (list, id) => list.find((x) => String(x.id) === String(id));
  const category = byId(categories, item.categoryId);
  const scope = {};
  if (item.itemType) scope.item_type = String(item.itemType).toLowerCase();
  if (category?.isDrugCategory != null) scope.is_drug_category = Boolean(category.isDrugCategory);
  const groupCode = byId(materialGroups, item.materialGroupId)?.code;
  const classCode = byId(itemClasses, item.itemClassId)?.code;
  if (groupCode) scope.material_group_code = groupCode;
  if (classCode) scope.item_class_code = classCode;
  if (category?.code) scope.inventory_category_code = category.code;
  if (item.code) scope.inventory_item_code = item.code;
  return scope;
}

/**
 * POST .../billing/pharmacy-charge-capture/
 * body: { items: [{ item_id, item_name, batches: [{ quantity, unit_rate,
 *   batch_number?, expiry_date? }], ...scope }], charge_date?, remarks? }
 * Scope fields (from deriveInventoryScope) may sit on each item.
 */
export async function postPharmacyChargeCapture(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/pharmacy-charge-capture/", payload);
  return unwrapObject(data);
}

/** POST .../billing/ot-charge-capture/ — single or bulk `{ charges: [...] }`. */
export async function captureOtCharges(admissionId, payload) {
  const data = await billingRequest("POST", admissionId, "/ot-charge-capture/", payload);
  return unwrapObject(data);
}

// ── Mappers (API → UI) ────────────────────────────────────────────────────────

const INVOICE_STATUS_LABEL = {
  DRAFT: "Draft",
  ISSUED: "Awaiting Settlement",
  PARTIALLY_PAID: "Partially Paid",
  PAID: "Paid",
  CANCELLED: "Cancelled",
  VOID: "Voided",
};

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fmtDateTime(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function pickLineItems(screen) {
  const candidates = [
    screen?.items,
    screen?.line_items,
    screen?.invoice_items,
    screen?.invoice?.items,
    screen?.invoice?.line_items,
    screen?.invoice?.invoice_items,
    screen?.invoice_view?.items,
  ];
  return candidates.find((a) => Array.isArray(a) && a.length > 0) || [];
}

// Adhoc discounts post as a separate ledger row per service (same name, rate 0,
// negative amount). Fold those into the matching charge so the bill reads as
// one row: rate / discount / net — instead of a duplicate discount line.
function isDiscountLine(i) {
  return Number(i.amount) < -0.009 && Number(i.rate) === 0 && !i.is_package;
}

function sameInvoiceService(a, b) {
  // Called as (charge, discount). An adhoc discount given on a PACKAGE posts as its own
  // BILLING line named after the package ("Package adhoc discount – <name>") with no service
  // code and no service id, so none of the tests below can tie it to the collapsed package
  // row it actually reduced. It stayed an orphan line, which the bill print drops as a
  // negative row — so the package printed at full price showing no discount at all.
  if (a.is_package) {
    const pkgName = String(a.description || "").trim().toLowerCase();
    return pkgName.length > 0
      && String(b.description || "").trim().toLowerCase().endsWith(pkgName);
  }
  if (a.medical_service_id && b.medical_service_id
    && String(a.medical_service_id) === String(b.medical_service_id)) return true;
  if (a.service_code && b.service_code
    && String(a.service_code).toUpperCase() === String(b.service_code).toUpperCase()) return true;
  return String(a.description || "").trim().toLowerCase() === String(b.description || "").trim().toLowerCase()
    && String(a.dept || "").trim().toLowerCase() === String(b.dept || "").trim().toLowerCase();
}

function foldDiscountLineItems(items) {
  const charges = [];
  const discounts = [];
  (items || []).forEach((i) => {
    if (isDiscountLine(i)) discounts.push(i);
    else charges.push({ ...i, discount: num(i.discount) });
  });
  if (discounts.length === 0) return charges;

  // Sibling discount rows are the source of truth — ignore any discount_amount
  // already on the charge so we don't double-count if both are present.
  charges.forEach((c) => { c.discount = 0; });

  const leftover = [];
  discounts.forEach((d) => {
    let remaining = Math.abs(num(d.amount));
    for (let i = 0; i < charges.length && remaining > 0.009; i += 1) {
      const c = charges[i];
      if (!sameInvoiceService(c, d)) continue;
      const room = Math.max(0, num(c.amount) - num(c.discount));
      if (room <= 0.009) continue;
      const take = Math.min(room, remaining);
      c.discount = num(c.discount) + take;
      remaining -= take;
    }
    if (remaining > 0.009) leftover.push({ ...d, is_discount: true });
  });
  return leftover.length ? [...charges, ...leftover] : charges;
}

// Display only: collapse a bed's per-day invoice lines (service code BED-*) into ONE line —
// quantity = number of days, amount = the sum. Mirrors the Charge Capture screen. The underlying
// per-day charges are untouched; this only changes how the bill/print reads. Non-bed lines and
// package lines pass through unchanged; order is preserved.
function aggregateBedLineItems(items) {
  const isBed = (i) =>
    !i.is_package && !i.is_discount && Number(i.amount) >= 0
    && String(i.service_code || "").toUpperCase().startsWith("BED-");
  const out = [];
  const groups = new Map(); // bed service code -> aggregated line (also present in `out`)
  (items || []).forEach((i) => {
    if (!isBed(i)) { out.push(i); return; }
    const key = i.service_code;
    const agg = groups.get(key);
    if (!agg) {
      groups.set(key, { ...i, bed_days: 1, discount: num(i.discount) });
      out.push(groups.get(key));
    } else {
      agg.qty = num(agg.qty) + num(i.qty);
      agg.amount = num(agg.amount) + num(i.amount);
      agg.gross = num(agg.gross) + num(i.gross);
      agg.discount = num(agg.discount) + num(i.discount);
      agg.bed_days += 1;
    }
  });
  return out;
}

export function mapInvoiceScreenToView(screen, admissionId) {
  const normalized = normalizeInvoiceScreenResponse(screen);
  const inv = normalized.invoice || {};
  const patient = normalized.patient || {};
  const doctor = normalized.doctor || {};
  const payer = normalized.payer || {};
  const hospital = normalized.hospital || normalized.tenant || {};
  const summary = normalized.summary || normalized.totals || {};

  const lineItems = pickLineItems(normalized).map((item, idx) => {
    // Scheme alias (item 6): a scheme patient's invoice prints the scheme's own
    // service name/code (display_*), snapshotted at generation. Fall back to the
    // hospital name/code when there is no alias. Never re-looks-up the mapping —
    // the frozen display_* values already carry the printed text.
    const hospitalName =
      item.hospital_service_name ||
      item.service_name ||
      item.serviceName ||
      item.name ||
      "—";
    const isScheme = String(item.service_name_source || "").toUpperCase() === "SCHEME";
    return {
      id: item.id || item.invoice_item_id || item.line_number || idx + 1,
      // `service_name` BEFORE `description`: the invoice service's /view and /screen
      // payloads already resolve the alias into `service_name` (falling back to the
      // hospital name) and leave `description` as the hospital's own text. Reading
      // `description` first printed the hospital name on every scheme invoice —
      // display_service_name is not even present in those payloads.
      description:
        item.display_service_name ||
        item.service_name ||
        item.description ||
        item.serviceName ||
        item.name ||
        "—",
      // Muted secondary line on scheme rows: show the hospital's own name so staff
      // can reconcile the printed scheme name against the internal catalogue.
      hospital_service_name: isScheme && hospitalName !== "—" ? hospitalName : null,
      // billingPdf reads `isScheme` to print the scheme's own code under the name;
      // it was never set on the mapped line, so that branch never fired.
      isScheme,
      service_name_source: item.service_name_source || null,
      dept: item.dept || item.category || item.department || "—",
      service_code:
        item.display_service_code || item.service_code || item.serviceCode || item.code || null,
      medical_service_id:
        item.medical_service_id || item.medicalServiceId || item.service_id || null,
      qty: num(item.quantity ?? item.qty ?? 1),
      rate: num(item.unit_price ?? item.rate ?? item.unitPrice),
      amount: num(item.net_amount ?? item.amount ?? item.netAmount ?? item.gross_amount),
      discount: num(item.discount_amount ?? item.line_discount ?? item.discount ?? 0),
      // Gross (list price) is kept only to detect package-covered lines below —
      // stripped from the view objects before they're returned.
      gross: num(item.gross_amount ?? item.grossAmount ?? item.gross ?? item.net_amount ?? item.amount),
      // What the package actually did with this line. Until now the invoice sent these as
      // null on every row, so the only way to spot a covered service was to guess from its
      // having a price but costing nothing — which cannot tell a covered service apart from
      // one that is genuinely free, and cannot see a part-covered line at all. Kept off the
      // returned view objects, like `gross`.
      is_package_charge: Boolean(item.is_package_charge),
      is_included: item.is_included_in_package === true,
      out_of_package: num(item.package_out_of_package_amount),
    };
  });

  // ── Package collapse ──────────────────────────────────────────────────────
  // When a package is assigned to the admission, the invoice lists every covered
  // service at net_amount 0 (the package's applied price already pays for them).
  // Collapse those covered lines into a single package line priced at the applied
  // price, so the bill reads: Package + any out-of-package services = net payable.
  const pkg = normalized.package || {};
  const packagePrice = num(pkg.package_applied_price);
  const hasPackage =
    Boolean(pkg.package_assignment_id || pkg.package_code) && packagePrice > 0;
  // One line per assigned package (a patient can have multiple). Falls back to the single
  // header package for older invoices without the per-package breakdown.
  const packageList = Array.isArray(pkg.packages) && pkg.packages.length > 0
    ? pkg.packages
    : [{ package_assignment_id: pkg.package_assignment_id, package_code: pkg.package_code, package_name: pkg.package_name, package_applied_price: packagePrice }];
  const displayItems = hasPackage
    ? [
      ...packageList.map((p) => ({
        id: `PKG-${p.package_assignment_id || p.package_code}`,
        description: p.package_name || p.package_code || "Package",
        dept: "PACKAGE",
        service_code: p.package_code || null,
        medical_service_id: null,
        qty: 1,
        rate: num(p.package_applied_price),
        amount: num(p.package_applied_price),
        is_package: true,
      })),
      // Keep only what the package did not pay for. An invoice raised since the package
      // flags started being recorded says so outright: a line the package covered in full
      // is folded into the package line above, and a part-covered line stays because the
      // patient genuinely owes the remainder. Older invoices carry no flags, so those fall
      // back to the old guess — a line with a price that costs nothing was covered.
      // Discount rows (negative, rate 0) are kept either way so they can fold onto the
      // charges that remain.
      ...lineItems.filter((i) => {
        if (isDiscountLine(i)) return true;
        if (i.is_package_charge) return false;
        if (i.is_included) return i.out_of_package > 0;
        return !(i.amount <= 0 && i.gross > 0);
      }),
    ]
    : lineItems;
  // Fold per-service discount rows into the matching charge, then collapse
  // each bed's per-day lines into one (qty = days, amount = sum) — display only.
  const aggregatedItems = aggregateBedLineItems(foldDiscountLineItems(displayItems));
  const finalLineItems = aggregatedItems.map(
    // eslint-disable-next-line no-unused-vars
    ({ gross: _gross, is_package_charge: _pkg, is_included: _inc, out_of_package: _oop, ...rest }) => rest
  );

  const gross = num(
    inv.gross_amount ?? summary.gross_amount ?? normalized.gross_amount
  );
  const apiDiscount = num(
    inv.discount_amount ?? summary.discount_amount ?? normalized.discount_amount
  );
  const foldedDiscount = finalLineItems.reduce((s, i) => {
    if (Number(i.discount) > 0) return s + Number(i.discount);
    if (Number(i.amount) < 0) return s + Math.abs(Number(i.amount));
    return s;
  }, 0);
  // A discount arrives as a credit, so the invoice reports it NEGATIVE. Testing it with
  // `> 0` therefore threw away the real figure and fell back to adding up the line rows -
  // which works for a discount given on a service, and finds nothing at all for one given on
  // a package, because a package discount has no service line to sit on. That is why a
  // discount on a package was missing from the printed bill while the total was correct.
  // Take whichever figure is actually there, as a positive amount.
  const reportedDiscount = Math.abs(apiDiscount);
  const discount = reportedDiscount > 0 ? reportedDiscount : foldedDiscount;
  const tax = num(
    inv.tax_amount ?? summary.tax_amount ?? normalized.tax_amount
  );
  const net = num(
    inv.net_amount ?? summary.net_amount ?? normalized.net_amount
  );
  const paid = num(inv.paid_amount ?? summary.paid_amount);
  const outstanding = num(
    inv.outstanding_amount ?? summary.outstanding_amount ?? normalized.outstanding_amount
  );
  const depositsAdjusted = num(
    normalized.deposits_adjusted ??
    normalized.deposit_utilized_amount ??
    summary.deposits_adjusted
  );

  const hospitalName =
    hospital.tenant_name ||
    hospital.name ||
    sessionStorage.getItem("facility_name") ||
    "Hospital";
  const hospitalInitial =
    hospital.initial || String(hospitalName).charAt(0).toUpperCase();

  const invoiceStatus = inv.invoice_status || "";
  const payerType = formatPayerType(payer.payer_type || payer.type);

  return {
    invoice_id: inv.id || inv.invoice_id || normalized.invoice_id || null,
    invoice_no: inv.invoice_number || inv.invoice_no || normalized.invoice_number || "—",
    encounter_id:
      normalized.encounter?.encounter_number ||
      normalized.encounter?.external_encounter_id ||
      admissionId,
    issued_date: fmtDate(inv.issued_at || inv.invoice_date || normalized.issued_at),
    status: normalized.effective_status || INVOICE_STATUS_LABEL[invoiceStatus] || invoiceStatus || "—",
    invoice_status: invoiceStatus,
    invoice_type: inv.invoice_type || normalized.invoice_type || "",
    hospital: {
      name: hospitalName,
      initial: hospitalInitial,
      address: hospital.address || "",
      gstin: hospital.gst_number || hospital.gstin || hospital.gst_in || "",
      color: hospital.color || "#0e9f8a",
    },
    patient: {
      name: patient.patient_name || patient.name || "—",
      age:
        patient.age != null
          ? String(patient.age)
          : ageFromDob(patient.date_of_birth || patient.dob),
      gender: formatGender(patient.gender || patient.sex),
      uhid:
        patient.uhid ||
        patient.patient_uhid ||
        patient.external_patient_id ||
        patient.patient_id ||
        "—",
      abha: Boolean(patient.abha || patient.abha_verified),
    },
    doctor: {
      name: doctor.name || doctor.doctor_name || "—",
      specialty: doctor.specialty || doctor.speciality || "—",
      ward: doctor.ward || doctor.ward_bed || "—",
    },
    payer: {
      name: payer.payer_name || payer.name || payerType || "Cash",
      type: payerType || "—",
      policy: payer.policy || payer.policy_number || "—",
      preauth: num(payer.preauth || payer.pre_auth_amount),
    },
    line_items: finalLineItems,
    // The same bill arranged into the hospital's own sections - bed, theatre,
    // investigations, procedures, drugs, consultation - each with its subtotals, and the
    // package heading its bill. The printed bill draws this when it is there and falls
    // back to grouping the flat list when it is not, so an older server still prints.
    bill_layout: normalized.bill_layout || screen?.bill_layout || null,
    // Which part of the admission this bill covers, so the print can say so. ALL is the
    // whole bill and needs no caption; the two halves of a split bill do.
    invoice_scope: String(inv.invoice_scope || normalized.invoice_scope || "ALL").toUpperCase(),
    // With a package, the covered lines are folded into the package line, so the
    // subtotal is the sum of what's actually shown (package + out-of-package),
    // which reconciles to the net payable. Without a package, keep the gross.
    subtotal: hasPackage
      ? finalLineItems.reduce((s, i) => s + i.amount, 0)
      : gross || lineItems.reduce((s, i) => s + i.amount, 0),
    discount,
    discount_label: normalized.discount_label || inv.discount_label || "Discount",
    gst_rate: num(normalized.gst_rate ?? inv.gst_rate),
    gst_amount: tax,
    total_payable: net || Math.max(0, gross - discount + tax),
    paid_amount: paid,
    outstanding_amount: outstanding || net,
    deposits_adjusted: depositsAdjusted,
    // Derive from the invoice net (not the backend's outstanding_amount): on a
    // package invoice the outstanding figure excludes the package's applied price, so it
    // reads e.g. 1,100 instead of 51,100. Subtract only `paid` — an adjusted deposit is
    // already posted into paid_amount, so subtracting deposits_adjusted too would
    // double-count it (net 1260 − 500 − 500 = 260 instead of the real 760).
    net_payable: Math.max(0, net - paid),
    // ── IQ-format printed-bill extras (consumed only by InvoicePrintSheet) ──
    deposits: (normalized.deposits || []).map((d) => ({
      receipt_no: d.receipt_no || d.deposit_number || "—",
      date: fmtDate(d.date),
      // Same lookup the deposits screen uses (mapDepositToTransaction) — the mode can
      // sit on the record or on a nested payment object depending on the endpoint.
      mode: formatPaymentModeLabel(pickPaymentModeFromRecord(d)),
      amount: num(d.amount),
      utilized: num(d.utilized),
      balance: num(d.balance),
    })),
    payments: (normalized.payments || []).map((p) => ({
      number: p.number || "—",
      mode: formatPaymentModeLabel(p.mode),
      type: p.type || "—",
      amount: num(p.amount),
      date: fmtDate(p.date),
    })),
    insurance_approved: num(normalized.insurance?.approved ?? normalized.totals?.insurance_approved),
    patient_payable_amt: num(normalized.insurance?.patient_payable ?? normalized.totals?.patient_payable),
    insurance_payer_name: normalized.insurance?.payer_name || payer.payer_name || null,
    // Whether this bill may be collected from the patient at all, decided by the backend so
    // the screen and the endpoints cannot disagree. Absent on an older response, which reads
    // as "allowed" and leaves the screen's own arithmetic in charge.
    patient_collection: normalized.patient_collection
      ? {
          allowed: normalized.patient_collection.allowed !== false,
          reason: normalized.patient_collection.reason || null,
          insurer_settled: !!normalized.patient_collection.insurer_settled,
          patient_payable: num(normalized.patient_collection.patient_payable),
        }
      : null,
    bill_header: {
      admission_date: fmtDateTime(normalized.bill_header?.admission_date),
      discharge_date: normalized.bill_header?.discharge_date
        ? fmtDateTime(normalized.bill_header.discharge_date)
        : "—",
      care_context: normalized.bill_header?.care_context || "—",
      ward_bed: normalized.bill_header?.ward_bed || doctor.ward || "—",
      patient_category: normalized.bill_header?.patient_category || payerType || "SELF PAY",
      phone: normalized.bill_header?.phone || "—",
      address: normalized.bill_header?.address || "—",
      corporate_name: normalized.bill_header?.corporate_name || null,
      // An emergency visit names its own dates (arrival and close).
      admission_label: normalized.bill_header?.admission_label || null,
      discharge_label: normalized.bill_header?.discharge_label || null,
    },
  };
}

export function mapDepositsToWallet(deposits = []) {
  const totalDeposits = deposits.reduce(
    (s, d) => s + num(d.deposit_amount ?? d.amount),
    0
  );
  const available = deposits.reduce(
    (s, d) => s + num(d.available_balance),
    0
  );
  const utilized = deposits.reduce(
    (s, d) => s + num(d.utilized_amount ?? d.deposit_utilized_amount),
    0
  );
  return {
    available_balance: available,
    total_deposits: totalDeposits,
    adjusted: utilized,
    deposits,
  };
}

function pickPaymentModeFromRecord(record) {
  const payment = record.payment || record.payment_details || {};
  return (
    record.payment_mode ||
    record.mode_of_payment ||
    record.mode ||
    record.payment_method ||
    payment.payment_mode ||
    payment.mode_of_payment ||
    payment.mode
  );
}

function mapDepositStatus(deposit) {
  const status = deposit.deposit_status;
  if (status === "ACTIVE" || status === "PARTIALLY_UTILIZED") return "Cleared";
  if (status === "REFUNDED") return "REFUNDED";
  return status || "Cleared";
}

export function mapDepositToTransaction(deposit, idx) {
  const mode = formatPaymentModeLabel(pickPaymentModeFromRecord(deposit));
  const rawReference =
    deposit.reference || deposit.transaction_reference || deposit.remarks || "";
  const reference =
    stripEmbeddedAmountFromText(rawReference) || rawReference || "—";
  const rawDate =
    deposit.date || deposit.created_at || deposit.payment_time || deposit.deposit_date;
  return {
    id: deposit.id || deposit.deposit_id || idx + 1,
    deposit_id: deposit.deposit_id || deposit.id,
    receipt:
      deposit.receipt ||
      deposit.deposit_number ||
      deposit.payment_number ||
      `DEP-${idx + 1}`,
    date: fmtDate(rawDate),
    mode,
    reference,
    collected_by:
      deposit.collected_by || deposit.created_by || deposit.created_by_name || "—",
    // amount is signed: DEPOSIT is +, ADJUSTED/REFUND come through as − from the backend.
    amount: num(deposit.amount ?? deposit.deposit_amount),
    status: deposit.status || mapDepositStatus(deposit),
    available_balance: num(deposit.available_balance),
    type: deposit.type || "DEPOSIT",
  };
}

export function mapRefundRecord(record, idx) {
  const mode = formatPaymentModeLabel(pickPaymentModeFromRecord(record));
  return {
    id: record.id || record.refund_id || idx + 1,
    ref_id: record.reference || record.refund_number || record.ref_id || record.payment_number || `REF-${idx + 1}`,
    amount: num(record.amount ?? record.refund_amount),
    patient: record.patient_name || record.patient || "Patient",
    mode,
    remark: stripEmbeddedAmountFromText(record.remarks || record.remark || record.subtitle || "") || record.remarks || record.remark || record.subtitle || "—",
    status: record.refund_status || record.status || "Processed",
    deposit_id: record.deposit_id,
  };
}

export function pickSplitBilling(screen) {
  const normalized = normalizeInvoiceScreenResponse(screen);
  const candidates = [
    normalized.split_billing,
    normalized.splitBilling,
    normalized.billing_split,
    screen?.split_billing,
  ];
  for (const entry of candidates) {
    if (Array.isArray(entry) && entry.length > 0) {
      return entry.map((row) => ({
        payer: row.label || row.payer || row.payer_name || "—",
        amount: num(row.amount ?? row.net_amount),
      }));
    }
  }
  return [];
}

export function buildSettlementSteps(invoice, payments = [], screen = null) {
  const apiSteps = screen?.settlement_status?.steps;
  if (Array.isArray(apiSteps) && apiSteps.length > 0) {
    return apiSteps.map((step, idx) => {
      const done = Boolean(step.done);
      const inProgress = !done && apiSteps.slice(0, idx).every((s) => s.done);
      return {
        label: step.label,
        status: done ? "done" : inProgress ? "in-progress" : "pending",
      };
    });
  }

  const status = String(invoice?.invoice_status || "").toUpperCase();
  const steps = [
    { label: "Bill generated", status: "done" },
    {
      label: "Invoice issued",
      status: ["ISSUED", "PARTIALLY_PAID", "PAID"].includes(status)
        ? "done"
        : status === "DRAFT"
          ? "pending"
          : "in-progress",
    },
  ];
  const depositsAdjusted = num(invoice?.deposits_adjusted);
  if (depositsAdjusted > 0) {
    steps.push({ label: "Deposit applied to bill", status: "done" });
  }
  if (payments.length > 0) {
    steps.push({ label: "Payment recorded", status: "done" });
  } else if (status === "PARTIALLY_PAID") {
    steps.push({
      label: depositsAdjusted > 0 ? "Partial settlement" : "Partial payment received",
      status: "in-progress",
    });
  } else if (status === "PAID") {
    steps.push({
      label: depositsAdjusted > 0 && payments.length === 0 ? "Settled from deposit" : "Fully settled",
      status: "done",
    });
  } else {
    steps.push({ label: "Payment pending", status: "pending" });
  }
  return steps;
}

export function pickPrimaryDeposit(deposits = []) {
  if (!deposits.length) return null;
  return [...deposits].sort(
    (a, b) => num(b.available_balance) - num(a.available_balance)
  )[0];
}

// ═════════════════════════════════════════════════════════════════════════════
// Cancel / Refund / Print / Scroll workflow
// (see ipd-billing-cancel-refund-scroll-ui-api.md)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Canonical billing permission codes (match billing_rbac_sync.py / spec Part A4).
 * Use with useBillingPermissions().can(BILLING_PERMS.RECEIPT_CANCEL_APPROVE).
 */
export const BILLING_PERMS = {
  RECEIPT_CANCEL_REQUEST: "RECEIPT_CANCEL_REQUEST",
  RECEIPT_CANCEL_APPROVE: "RECEIPT_CANCEL_APPROVE",
  RECEIPT_CANCEL_EXECUTE: "RECEIPT_CANCEL_EXECUTE",
  PAYMENT_REFUND_REQUEST: "PAYMENT_REFUND_REQUEST",
  PAYMENT_REFUND_APPROVE: "PAYMENT_REFUND_APPROVE",
  PAYMENT_REFUND_EXECUTE: "PAYMENT_REFUND_EXECUTE",
  DEPOSIT_TRANSFER_FROM_CANCELLED_RECEIPT: "DEPOSIT_TRANSFER_FROM_CANCELLED_RECEIPT",
  INVOICE_CANCEL_REQUEST: "INVOICE_CANCEL_REQUEST",
  INVOICE_CANCEL_APPROVE: "INVOICE_CANCEL_APPROVE",
  INVOICE_CANCEL_EXECUTE: "INVOICE_CANCEL_EXECUTE",
  DOCUMENT_PRINT: "DOCUMENT_PRINT",
  DOCUMENT_DUPLICATE_PRINT: "DOCUMENT_DUPLICATE_PRINT",
  PRINT_COUNTER_RESET_REQUEST: "PRINT_COUNTER_RESET_REQUEST",
  PRINT_COUNTER_RESET_APPROVE: "PRINT_COUNTER_RESET_APPROVE",
  PRINT_COUNTER_RESET_EXECUTE: "PRINT_COUNTER_RESET_EXECUTE",
  SCROLL_INTERIM_VIEW: "SCROLL_INTERIM_VIEW",
  SCROLL_INTERIM_PRINT: "SCROLL_INTERIM_PRINT",
  SCROLL_FINAL_VIEW: "SCROLL_FINAL_VIEW",
  SCROLL_FINAL_PRINT: "SCROLL_FINAL_PRINT",
  SCROLL_FINAL_CLOSE: "SCROLL_FINAL_CLOSE",
  SCROLL_ALL_COUNTERS_VIEW: "SCROLL_ALL_COUNTERS_VIEW",
};

/**
 * GET /ipd/billing/permissions/ — codes + role level for the current user
 * (not admission-scoped). Returns `{ codes: string[], roleLevel: number|null,
 * roleName: string }`. Empty codes => no access.
 */
export async function getMyBillingPermissions() {
  try {
    const res = await axios.get(`${API_ROOT}ipd/billing/permissions/`, {
      headers: getBillingHeaders(),
    });
    if (res.data?.status === "Failed") {
      throw new Error(res.data.message || "Failed to load permissions");
    }
    const d = res.data;
    // Tolerate several response shapes: a bare array, { permissions: [...] },
    // { data: { permissions } }, { data: [...] }. Items may be strings or objects.
    const raw = Array.isArray(d)
      ? d
      : d?.permissions ?? d?.data?.permissions ?? d?.data ?? d?.results ?? [];
    const list = Array.isArray(raw) ? raw : [];
    const codes = list.map((p) =>
      typeof p === "string"
        ? p
        : p?.code || p?.permission_code || p?.billing_code || p?.permission_name
    );
    // Role level lives at the top level; fall back to the max level on any item.
    const levelFromItems = list.reduce((max, p) => {
      const lv = typeof p === "object" ? Number(p?.role_level) : NaN;
      return Number.isFinite(lv) ? Math.max(max, lv) : max;
    }, NaN);
    const roleLevel = Number.isFinite(Number(d?.role_level))
      ? Number(d.role_level)
      : Number.isFinite(levelFromItems)
        ? levelFromItems
        : null;
    return {
      codes: [...new Set(codes.filter(Boolean).map((c) => String(c).toUpperCase()))],
      roleLevel,
      roleName: d?.role_name || "",
    };
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    const wrapped = new Error(
      err.response?.data?.message || err.message || "Failed to load permissions"
    );
    wrapped.status = err.response?.status;
    throw wrapped;
  }
}

/**
 * Approvals inbox feed (spec Part B2).
 *   - admission-scoped: GET /ipd_admission/{id}/billing/approval-requests/
 *   - tenant-wide:      GET /ipd/billing/approval-requests/
 * Approve/execute are admission-scoped, so pass `admissionId` to get an actionable
 * list; the tenant-wide list is view-only (rows carry no admission id yet).
 *
 * @param {{ admissionId?: string|number, queue?: "pending"|"completed"|"all", context?: string, mine?: boolean }} opts
 */
export async function listApprovalRequests({ admissionId, queue = "pending", context, mine } = {}) {
  const params = new URLSearchParams();
  if (queue) params.set("queue", queue);
  if (context) params.set("approval_context", context);
  if (mine) params.set("mine", "1");
  const qs = params.toString() ? `?${params.toString()}` : "";
  const url = admissionId
    ? `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing/approval-requests/${qs}`
    : `${API_ROOT}ipd/billing/approval-requests/${qs}`;
  try {
    const res = await axios.get(url, { headers: getBillingHeaders() });
    if (res.data?.status === "Failed") {
      throw new Error(sanitizeErrorMessage(res.data.message || "Failed to load approvals"));
    }
    const d = res.data?.data ?? res.data;
    return Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : [];
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    const wrapped = new Error(
      sanitizeErrorMessage(
        err.response?.data?.message || err.message || "Failed to load approvals",
        err.response?.status
      )
    );
    wrapped.status = err.response?.status;
    throw wrapped;
  }
}

/**
 * Cashier receipt-row workflow badges (spec Part B2) — one entry per payment with
 * `ui_badge_label`, `ui_badge_tone`, `receipt_cancel_status`, `approval_request_id`.
 * GET /ipd_admission/{id}/billing/workflow-statuses/
 */
export async function getWorkflowStatuses(admissionId) {
  const data = await billingRequest("GET", admissionId, "/workflow-statuses/");
  const d = unwrapObject(data);
  return Array.isArray(d?.items) ? d.items : [];
}

/**
 * One payment/receipt's workflow status — the per-receipt source (spec Part B2,
 * "details/polling"). Preferred over the admission-wide `workflow-statuses/` list.
 * GET /ipd_admission/{id}/billing/payments/{payment_id}/workflow-status/
 * Returns { ui_badge_label, ui_badge_tone, workflow_status/request_status, next_action, ... }.
 */
export async function getPaymentWorkflowStatus(admissionId, paymentId) {
  const data = await billingRequest(
    "GET",
    admissionId,
    `/payments/${trimId(paymentId)}/workflow-status/`
  );
  return unwrapObject(data);
}

// ── Receipt cancel (Part C / D) ───────────────────────────────────────────────

export async function requestReceiptCancel(admissionId, paymentId, payload) {
  // payload: { reason, requested_amount, post_cancel_action: REFUND|TRANSFER_TO_DEPOSIT|NO_REFUND }
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/receipts/${trimId(paymentId)}/cancel-requests/`,
      payload
    )
  );
}

export async function approveReceiptCancel(admissionId, requestId, payload) {
  // payload: { approve: true|false, reason }
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/receipt-cancel-requests/${trimId(requestId)}/approve/`,
      payload
    )
  );
}

export async function executeReceiptCancel(admissionId, requestId) {
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/receipt-cancel-requests/${trimId(requestId)}/execute/`,
      {}
    )
  );
}

export async function listReceiptCancelRequests(admissionId) {
  const data = await billingRequest("GET", admissionId, "/receipt-cancel-requests/");
  return unwrapList(data, ["receipt_cancel_requests", "cancel_requests", "requests"]);
}

export async function transferReceiptToDeposit(admissionId, paymentId, payload) {
  // payload: { transfer_amount, reason }
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/receipts/${trimId(paymentId)}/transfer-to-deposit/`,
      payload
    )
  );
}

// ── Payment refund (Part C) ───────────────────────────────────────────────────

export async function requestPaymentRefund(admissionId, paymentId, payload) {
  // payload: { refund_amount, reason }
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/payments/${trimId(paymentId)}/refund-requests/`,
      payload
    )
  );
}

export async function approvePaymentRefund(admissionId, refundId, payload) {
  // payload: { approve: true|false, reason? }
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/payment-refund-requests/${trimId(refundId)}/approve/`,
      payload
    )
  );
}

export async function createRefundInstruction(admissionId, refundId, payload) {
  // payload: { refund_mode, transaction_reference }
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/payment-refunds/${trimId(refundId)}/create-instruction/`,
      payload
    )
  );
}

// ── Invoice cancel (Part E) ───────────────────────────────────────────────────

export async function requestInvoiceCancel(admissionId, invoiceId, payload) {
  // payload: { reason }
  // Direct cancel: POST .../billing/invoices/{invoice_id}/cancel/ — cancels an
  // ISSUED (unsettled) invoice outright, without the request/approve/execute
  // approval workflow (and without its INVOICE_CANCEL_* permission checks).
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/invoices/${trimId(invoiceId)}/cancel/`,
      payload
    )
  );
}

export async function approveInvoiceCancel(admissionId, requestId, payload) {
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/invoice-cancel-requests/${trimId(requestId)}/approve/`,
      payload
    )
  );
}

export async function executeInvoiceCancel(admissionId, requestId) {
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/invoice-cancel-requests/${trimId(requestId)}/execute/`,
      {}
    )
  );
}

export async function listInvoiceCancelRequests(admissionId) {
  const data = await billingRequest("GET", admissionId, "/invoice-cancel-requests/");
  return unwrapList(data, ["invoice_cancel_requests", "cancel_requests", "requests"]);
}

// ── Print ORIGINAL / DUPLICATE / reset (Part F) ───────────────────────────────

export async function printDocumentFor(patient, payload) {
  // payload: { document_type, document_id, document_number, print_channel }
  // returns { print_label: ORIGINAL|DUPLICATE, print_count, ... } — an emergency
  // visit's bill is counted by the same print counter as an admission's.
  return unwrapObject(
    await subjectRequest("POST", patient, `/documents/print/`, {
      print_channel: "UI",
      ...payload,
    })
  );
}

export async function requestPrintResetFor(patient, docType, docId, payload) {
  // payload: { reason }
  return unwrapObject(
    await subjectRequest(
      "POST",
      patient,
      `/documents/${encodeURIComponent(docType)}/${trimId(docId)}/print-counter-reset-requests/`,
      payload
    )
  );
}

export async function approvePrintReset(admissionId, requestId, payload) {
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/print-counter-reset-requests/${trimId(requestId)}/approve/`,
      payload
    )
  );
}

export async function executePrintReset(admissionId, requestId) {
  return unwrapObject(
    await billingRequest(
      "POST",
      admissionId,
      `/print-counter-reset-requests/${trimId(requestId)}/execute/`,
      {}
    )
  );
}

// ── Cash counter & scroll (Part G) ────────────────────────────────────────────

// Cash counters & scrolls are tenant-scoped (not tied to a patient/admission):
//   POST /ipd/billing/cash-counters/{counter_code}/sessions/open/
//   GET  /ipd/billing/cash-counters/{counter_code}/scrolls/interim/?session_id=
//   POST /ipd/billing/cash-counters/{counter_code}/scrolls/interim/print/?session_id=
//   POST /ipd/billing/cash-counters/{counter_code}/scrolls/final/
//   POST /ipd/billing/cash-scrolls/{scroll_id}/finalize/

/** The logged-in cashier, as the counter list reports `opened_by`. */
function currentUserName() {
  try {
    return String(
      sessionStorage.getItem("userName") || sessionStorage.getItem("username") || ""
    ).trim();
  } catch {
    return "";
  }
}

/**
 * Occupancy of one counter, from whatever shape the list endpoint uses. A counter
 * is exclusive: while one cashier holds an OPEN session on it, nobody else may
 * open it (the server 409s; this is what lets the UI say so up front).
 *
 * Absent every occupancy field the counter reads as free — the open call is still
 * the authority, so a server that doesn't report occupancy degrades to the 409.
 */
function counterOccupancy(c) {
  const sess = c.open_session || c.current_session || c.active_session || null;
  const status = String(c.session_status ?? c.status ?? sess?.status ?? "").toUpperCase();
  const flag = c.is_open ?? c.has_open_session ?? c.session_open ?? c.is_occupied ?? null;

  const isOpen =
    flag != null
      ? Boolean(flag)
      : Boolean(sess) || ["OPEN", "OCCUPIED", "IN_USE", "BUSY"].includes(status);

  const openedBy = String(
    c.opened_by_name ?? c.opened_by ?? sess?.opened_by_name ?? sess?.opened_by ?? ""
  ).trim();
  const me = currentUserName();

  return {
    isOpen,
    openedBy: openedBy || null,
    openedAt: c.opened_at ?? c.session_opened_at ?? sess?.opened_at ?? null,
    sessionId: c.session_id ?? c.open_session_id ?? sess?.session_id ?? sess?.id ?? null,
    // Only claim the session is mine on a positive name match — an unattributed
    // open session belongs to someone else as far as this cashier is concerned.
    openedByMe: isOpen && Boolean(me) && openedBy.toLowerCase() === me.toLowerCase(),
  };
}

/**
 * G0 — list the cash counters the logged-in cashier may use.
 * GET /ipd/billing/cash-counters/  → counters (billing_user_counter_access) + default.
 * Each counter carries its occupancy so the selector can lock out a counter another
 * cashier already has open.
 * @returns {Promise<{ counters: Array<{ code: string, name: string, isOpen: boolean, openedBy: string|null, openedByMe: boolean, openedAt: string|null, sessionId: string|null }>, defaultCode: string|null }>}
 */
export async function listCashCounters() {
  const data = await billingTenantRequest("GET", "/cash-counters/");
  const obj = unwrapObject(data);
  const counters = unwrapList(obj, ["counters", "cash_counters"])
    .map((c) => {
      if (typeof c === "string") {
        return { code: c, name: c, isOpen: false, openedBy: null, openedByMe: false, openedAt: null, sessionId: null };
      }
      const code = c.counter_code ?? c.code ?? c.id ?? c.counter ?? "";
      return {
        code: String(code),
        name: c.counter_name ?? c.name ?? c.display_name ?? String(code),
        ...counterOccupancy(c),
      };
    })
    .filter((c) => c.code);

  // Never default onto a counter someone else is holding — that default lands the
  // cashier on a counter they can only fail to open.
  const free = counters.filter((c) => !c.isOpen || c.openedByMe);
  const serverDefault = obj.default_counter_code ?? obj.default_counter ?? null;
  const defaultIsFree = free.some((c) => c.code === serverDefault);

  return {
    counters,
    defaultCode: (defaultIsFree ? serverDefault : null) || free[0]?.code || serverDefault || counters[0]?.code || null,
  };
}

/**
 * G1 — open a counter session.
 * Body: { opening_cash_amount, remarks }. `counter_name` is optional — the server
 * defaults it to "Cash Counter {counter_code}" — so it's only sent when provided.
 * @param {{ openingBalance?: string|number, remarks?: string, counterName?: string }} opts
 */
export async function openCounterSession(counterCode, opts = {}) {
  const body = {
    opening_cash_amount: Number(opts.openingBalance ?? opts.opening_cash_amount ?? 0).toFixed(2),
  };
  if (opts.counterName) body.counter_name = opts.counterName;
  if (opts.remarks) body.remarks = opts.remarks;
  return unwrapObject(
    await billingTenantRequest(
      "POST",
      `/cash-counters/${encodeURIComponent(counterCode)}/sessions/open/`,
      body
    )
  );
}

export async function getInterimScroll(counterCode, sessionId) {
  return unwrapObject(
    await billingTenantRequest(
      "GET",
      `/cash-counters/${encodeURIComponent(counterCode)}/scrolls/interim/?session_id=${encodeURIComponent(sessionId)}`
    )
  );
}

export async function printInterimScroll(counterCode, sessionId) {
  return unwrapObject(
    await billingTenantRequest(
      "POST",
      `/cash-counters/${encodeURIComponent(counterCode)}/scrolls/interim/print/?session_id=${encodeURIComponent(sessionId)}`,
      {}
    )
  );
}

/**
 * G5 — create the final scroll for a session.
 * Body: `{ session_id, declared_cash_amount, remarks }`. `declared_cash_amount`
 * is REQUIRED by the backend (a 422 "Field required" otherwise).
 * @param {{ sessionId?: string, session_id?: string, declaredCash?: string|number, remarks?: string }} opts
 */
export async function createFinalScroll(counterCode, opts = {}) {
  const sessionId = opts.sessionId ?? opts.session_id;
  const body = {
    declared_cash_amount: Number(opts.declaredCash ?? opts.declared_cash_amount ?? 0).toFixed(2),
  };
  if (sessionId) body.session_id = sessionId;
  if (opts.remarks) body.remarks = opts.remarks;
  return unwrapObject(
    await billingTenantRequest(
      "POST",
      `/cash-counters/${encodeURIComponent(counterCode)}/scrolls/final/`,
      body
    )
  );
}

/**
 * G6 — finalize the scroll and close the session.
 * payload: { declared_cash, closing_remarks, variance_reason? }
 */
export async function finalizeScroll(scrollId, payload) {
  return unwrapObject(
    await billingTenantRequest(
      "POST",
      `/cash-scrolls/${trimId(scrollId)}/finalize/`,
      payload
    )
  );
}

// ── Friendly error mapping (Part H) ───────────────────────────────────────────

/**
 * Turn a thrown billing error into a short, user-facing message.
 * Errors from billingRequest carry `.status` (HTTP code) and `.message`.
 */
export function toBillingError(err, fallback = "Something went wrong") {
  const status = err?.status;
  const raw = String(err?.message || "").toLowerCase();

  // A shut counter, before the status-code rules below get a chance to reword it.
  if (err?.cashCounterRequired) return CASH_COUNTER_CLOSED_HINT;

  // Maker–checker rule violations can come back as 403 or 409.
  if (raw.includes("self")) return "You can't approve your own request";
  if (raw.includes("role level") || raw.includes("level too low") || raw.includes("seniority") || raw.includes("higher approver"))
    return "A higher approver level is required for this amount";

  if (status === 403) {
    return "You don't have permission for this action";
  }
  if (status === 409) {
    if (raw.includes("receipt") || raw.includes("allocation") || raw.includes("payment"))
      return "Cancel receipt(s) before cancelling this invoice";
    if (raw.includes("already") || raw.includes("finalize"))
      return "This item was already actioned";
    if (raw.includes("refund") && raw.includes("amount"))
      return "Refund amount exceeds the paid amount";
    return "This action isn't allowed in the current state";
  }
  if (status === 404) return "Record not found";
  if (status === 500 || raw.includes("internal server error"))
    return "Server error — please try again or contact support.";
  if (raw.includes("billing account") || raw.includes("billing_setup"))
    return "Billing is not set up for this admission";
  if (raw.includes("charge_modifiable") || raw.includes("force_rate_edit"))
    return "This service's rate is locked — turn on \"Rate modifiable\" or confirm the rate override to change the fee.";

  // Fall back to the server message if it looks human-readable, else generic.
  return err?.message && err.message.length < 140 ? err.message : fallback;
}
